/**
 * TemplatesWorkspace — Checklist Template editor
 * Opened from SiteNav or a milestone's "Edit template" menu item. Full-screen workspace
 * overlay. Gated on the 'admin' OR 'checklist-templates' module (2026-07 — Manager seats
 * get the latter without full admin; see database/131_checklist_templates_module.sql).
 * Three views: list (milestone / drawing sheet tabs), ms-editor, ds-editor.
 */
import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'
import {
  getDeliverableTypes,
  getDrawingSheetTemplates,
  getMilestoneTemplateItems,
  getDrawingTemplateItems,
  createDeliverableType,
  createDrawingSheetTemplate,
  renameDeliverableType,
  saveTemplateItem,
  deleteTemplateItem,
  patchItemSortOrder,
  analyzePushMilestone,
  analyzePushDrawing,
  doPushMilestone,
  doPushDrawing,
} from '../services/templates.js'

import MilestoneTypeKindCard from './MilestoneTypeKindCard.jsx'
import { confirmDialog, promptDialog } from './Dialog.jsx'
import { CAT_FULL_KIT, CAT_ORDER, catLabel } from '../outline/constants.js'
import { ctx } from '../outline/ctx.js'
import { BimTemplatesList, BimTemplateEditorView } from './BimTemplateEditor.jsx'
import { SheetTemplateTitleblockPanel } from './TitleblockProfileEditor.jsx'
import PunchRoomGuidanceList from './PunchRoomGuidanceEditor.jsx'
import DemoKeynotesList from './DemoKeynotesEditor.jsx'
import DrawingAbbreviationsList from './DrawingAbbreviationsEditor.jsx'
import SheetNotesList from './SheetNotesEditor.jsx'
import SubmittalChecklistList from './SubmittalChecklistEditor.jsx'
import PriorityScoreEditor from './PriorityScoreEditor.jsx'
import { listV2TemplateItems, addV2TemplateItem, updateV2TemplateItem, removeV2TemplateItem, reorderV2TemplateItems, analyzePushV2, doPushV2 } from '../services/checklistTemplates.js'
import { TypeKitEditor } from './GateTemplateEditor.jsx'
import { WorkspaceHeader } from './WorkspaceChrome.jsx'

// Modeler firm library (family-management round) — lazy: it pulls in @aj/bim-kernel
// (yjs) and Modeler panel modules, which don't belong in the main bundle
const BimFirmLibraryList = React.lazy(() => import('../bim/BimFirmLibraryEditor.jsx'))
// Firm detail library (DETAIL LIBRARY round, 2026-10-26) — the same lazy reason
const FirmDetailLibraryTab = React.lazy(() => import('../bim/FirmDetailLibrary.jsx'))

// ─── Constants ────────────────────────────────────────────────────────────────
const PHASES = [
  'Pre-design','Schematic Design','Discretionary Approvals','Design Development',
  'Construction Documents','Permitting','Architectural Detailing','Interior Architecture',
  'Bidding and Negotiation','Construction Support','Additional Services',
  'Existing Building 3D Model',
]
const BEHAVIORS = [
  'Standard','Permit','Revision','BIM','Interior Concept Package',
  'Plan Check Submission','Construction Administration',
]
const DISCIPLINES = [
  'General','Special General','As-Built','Architectural','Demolition',
  'Architectural Detailing','Interior Architecture','Energy','Civil','Electrical',
  'Mechanical','Structural','Landscape','Plumbing','Other',
]
// Moved in from Admin → Templates (Matt, 2026-09-24): one place for templates, open to anyone who
// can use this tool (Matt, same day). Each opens its existing editor on top.
const ADMIN_TEMPLATE_TABS = [
  { key: 'wbs', label: 'WBS Milestones', event: 'open-wbs-editor', title: 'WBS Milestone Templates',
    blurb: 'Which milestones each phase of a new project gets, their order, how long each takes, and what waits on what. Edits apply the next time a phase is set up.' },
  { key: 'rooms', label: 'Rooms', event: 'open-room-templates-editor', title: 'Room Templates',
    blurb: 'The room types every Room and the Building Information Model pick from: name, project type, the Room Book flag, and each type’s structural base factor (it feeds the room’s cost factor).' },
  { key: 'budget', label: 'Budget Models', event: 'open-control-budget-models-editor', title: 'Control Budget Models',
    blurb: 'The cost-per-square-foot models a Concept uses: category rates, scope factors, the gross area factor, and pool and spa lump sums. They drive the Control Budget and the Building Information Model.' },
]
const ADMIN_TAB_KEYS = new Set(ADMIN_TEMPLATE_TABS.map(t => t.key))

function AdminTemplateLauncher({ tab }) {
  const t = ADMIN_TEMPLATE_TABS.find(x => x.key === tab)
  if (!t) return null
  return (
    <div style={{ padding: '18px 24px' }}>
      <div style={{ maxWidth: 720, padding: '14px 18px', borderRadius: 10, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', fontFamily: 'DM Sans, sans-serif' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--aj-text)' }}>{t.title}</div>
        <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginTop: 4, lineHeight: 1.5 }}>{t.blurb}</div>
        <button onClick={() => window.dispatchEvent(new CustomEvent(t.event))} style={{ ...S.btnPrimary, marginTop: 12 }}>Open editor</button>
      </div>
    </div>
  )
}

const CATEGORIES = CAT_ORDER   // stored values; render with catLabel
const RELEASES = ['AB','DAPC','PPC / BID','EXT','INT']


// ─── Data normalizers ─────────────────────────────────────────────────────────
function normalizeMsTemplate(r) {
  const phase = Array.isArray(r.fields['Phase']) ? r.fields['Phase'][0] : (r.fields['Phase'] || '')
  const behavior = Array.isArray(r.fields['Milestone Behavior']) ? r.fields['Milestone Behavior'][0] : (r.fields['Milestone Behavior'] || '')
  return { id: r.id, name: r.fields['Name'] || '', phase, behavior }
}
function normalizeDsTemplate(r) {
  const num = r.fields['Sheet Number'] || ''
  const title = r.fields['Sheet Name'] || ''
  return {
    id: r.id,
    number: num,
    title,
    name: [num, title].filter(Boolean).join(' — ') || '(Untitled)',
    discipline: Array.isArray(r.fields['Discipline']) ? r.fields['Discipline'][0] : (r.fields['Discipline'] || ''),
  }
}
function normalizeItem(r) {
  return {
    id: r.id,
    name: r.fields['Name'] || '',
    summary: r.fields['Summary'] || '',
    category: r.fields['Category'] || CAT_FULL_KIT,
    sortOrder: typeof r.fields['Sort Order'] === 'number' ? r.fields['Sort Order'] : 0,
    conditionQuestion: r.fields['Condition Question'] || '',
    conditionAnswer: r.fields['Condition Answer'] || '',
    conditionParent: Array.isArray(r.fields['Condition Parent']) ? (r.fields['Condition Parent'][0] || null) : null,
    exampleImages: r.fields['Example Images'] || [],
    drawingReleases: r.fields['Drawing Releases'] || [],
    _dirty: false,
    _imagesDirty: false,
  }
}

// Sheet 4 part two, step 3 (docs/CHECKLIST_PORT.md): milestone templates are the v2 Supabase rows —
// one flat list, no categories, no conditional logic. Shaped like normalizeItem's output so ItemRow,
// AddItemRow and DragList need no second code path; the `_v2` flag hides the fields v2 does not have.
function normalizeV2Item(r) {
  return {
    id: r.id, name: r.name || '', summary: r.summary || '', category: 'Execution',
    sortOrder: typeof r.sort_order === 'number' ? r.sort_order : 0,
    conditionQuestion: '', conditionAnswer: '', conditionParent: null,
    exampleImages: r.example_image_url ? [{ url: r.example_image_url, filename: 'example' }] : [],
    drawingReleases: r.drawing_releases || [], _dirty: false, _imagesDirty: false, _v2: true,
  }
}

// ─── Shared inline styles ─────────────────────────────────────────────────────
export const S = {
  label: { display: 'block', fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: { display: 'block', width: '100%', padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { padding: '6px 14px', background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', flexShrink: 0 },
  btnSecondary: { padding: '6px 12px', background: 'transparent', color: 'var(--aj-text)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontSize: 13, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', flexShrink: 0 },
  pill: (active) => ({
    padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12,
    fontFamily: 'DM Sans, sans-serif',
    background: active ? '#07272D' : 'var(--aj-bg-muted)',
    color: active ? '#fff' : 'var(--aj-text-mid)',
  }),
}

// ─── Toast system ─────────────────────────────────────────────────────────────
function Toasts({ toasts }) {
  if (!toasts.length) return null
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 600, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '9px 18px', borderRadius: 6, fontSize: 13, fontFamily: 'DM Sans, sans-serif',
          background: t.type === 'error' ? '#d9534f' : '#07272D', color: '#fff',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>
          {t.msg}
        </div>
      ))}
    </div>
  )
}

function useToasts() {
  const [toasts, setToasts] = useState([])
  const addToast = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random()
    setToasts(t => [...t, { id, msg, type }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500)
  }, [])
  return { toasts, addToast }
}

// ─── DragList ─────────────────────────────────────────────────────────────────
// Exported for the BIM templates tab (BimTemplateEditor.jsx) — same drag affordance
// across every template domain.
export function DragList({ items, onReorder, renderItem }) {
  const [draggingId, setDraggingId] = useState(null)
  const [dropBeforeId, setDropBeforeId] = useState(null)
  const containerRef = useRef()

  function handleHandlePointerDown(e, id) {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    setDraggingId(id)
    setDropBeforeId(null)
    // Capture on the container so move/up fire reliably
    containerRef.current?.setPointerCapture(e.pointerId)
  }

  function handlePointerMove(e) {
    if (!draggingId) return
    const rows = containerRef.current ? Array.from(containerRef.current.querySelectorAll('[data-drag-id]')) : []
    let found = null
    for (const row of rows) {
      const rect = row.getBoundingClientRect()
      if (e.clientY < rect.top + rect.height / 2) {
        found = row.dataset.dragId
        break
      }
    }
    if (found !== dropBeforeId) setDropBeforeId(found)
  }

  function handlePointerUp() {
    if (!draggingId) return
    const dragIdx = items.findIndex(x => x.id === draggingId)
    if (dragIdx !== -1) {
      const without = items.filter(x => x.id !== draggingId)
      let newItems
      if (dropBeforeId === null) {
        newItems = [...without, items[dragIdx]]
      } else {
        const dropIdx = without.findIndex(x => x.id === dropBeforeId)
        if (dropIdx === -1) newItems = [...without, items[dragIdx]]
        else { newItems = [...without]; newItems.splice(dropIdx, 0, items[dragIdx]) }
      }
      onReorder(newItems)
    }
    setDraggingId(null)
    setDropBeforeId(null)
  }

  return (
    <div
      ref={containerRef}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {items.map(item => (
        <div key={item.id} data-drag-id={item.id} style={{ position: 'relative' }}>
          {dropBeforeId === item.id && draggingId && draggingId !== item.id && (
            <div style={{ height: 2, background: '#07272D', borderRadius: 1, margin: '0 8px', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
          )}
          {renderItem(
            item,
            { onPointerDown: (e) => handleHandlePointerDown(e, item.id) },
            draggingId === item.id
          )}
        </div>
      ))}
      {dropBeforeId === null && draggingId && items.length > 0 && (
        <div style={{ height: 2, background: '#07272D', borderRadius: 1, margin: '0 8px' }} />
      )}
    </div>
  )
}

// ─── ItemRow ──────────────────────────────────────────────────────────────────
function ItemRow({ item, allItems, type, expanded, onToggleExpand, onSave, onDelete, dragHandleProps, isDragging }) {
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (expanded && draft === null) {
      setDraft({
        name: item.name,
        summary: item.summary || '',
        category: item.category || CAT_FULL_KIT,
        drawingReleases: item.drawingReleases || [],
        conditionQuestion: item.conditionQuestion || '',
        conditionAnswer: item.conditionAnswer || '',
        conditionParent: item.conditionParent || '',
      })
    }
    if (!expanded) setDraft(null)
  }, [expanded]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!draft) return
    setSaving(true)
    try {
      await onSave({ ...item, ...draft, conditionParent: draft.conditionParent || null })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ userSelect: 'none' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '7px 8px 7px 0',
        borderBottom: '0.5px solid var(--aj-border)',
        opacity: isDragging ? 0.35 : 1,
        background: expanded ? 'var(--aj-bg-subtle)' : 'transparent',
        transition: 'opacity 0.1s',
      }}>
        <div {...dragHandleProps} style={{ cursor: 'grab', padding: '2px 8px', color: 'var(--aj-text-muted)', fontSize: 14, flexShrink: 0, touchAction: 'none' }}>⠿</div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: item.name ? 'var(--aj-text)' : 'var(--aj-text-muted)' }}>
          {item.name || 'Untitled item'}
          {type === 'ms' && !item._v2 && item.category && (
            <span style={{ marginLeft: 8, fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)' }}>{catLabel(item.category)}</span>
          )}
          {type === 'ds' && item.drawingReleases?.length > 0 && (
            <span style={{ marginLeft: 8, fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)' }}>{item.drawingReleases.join(' · ')}</span>
          )}
          {item.conditionQuestion && (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--aj-text-muted)' }} title={`Conditional: ${item.conditionQuestion}`}>⊃</span>
          )}
        </div>
        <button
          onClick={onToggleExpand}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', color: expanded ? 'var(--aj-text)' : 'var(--aj-text-muted)', fontSize: 12 }}
          title={expanded ? 'Close' : 'Edit'}
        >{expanded ? '▲' : '▼'}</button>
        <button
          onClick={() => onDelete(item)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', color: 'var(--aj-text-muted)', fontSize: 12 }}
          title="Delete item"
        >✕</button>
      </div>

      {expanded && draft && (
        <div style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, padding: '14px 16px 12px', margin: '4px 0 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Item Name</label>
              <input style={S.input} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Item name" />
            </div>
            {type === 'ms' && !item._v2 && (
              <div style={{ width: 140 }}>
                <label style={S.label}>Category</label>
                <select style={S.input} value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <label style={S.label}>Summary / Instructions</label>
            <textarea style={{ ...S.input, minHeight: 64, resize: 'vertical' }} value={draft.summary} onChange={e => setDraft(d => ({ ...d, summary: e.target.value }))} placeholder="Optional description or checklist instructions" />
          </div>

          {type === 'ds' && (
            <div>
              <label style={S.label}>Drawing Releases</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {RELEASES.map(r => {
                  const on = draft.drawingReleases.includes(r)
                  return (
                    <button key={r} onClick={() => setDraft(d => ({ ...d, drawingReleases: on ? d.drawingReleases.filter(x => x !== r) : [...d.drawingReleases, r] }))}
                      style={{ padding: '4px 11px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', background: on ? '#07272D' : 'transparent', color: on ? '#fff' : 'var(--aj-text)', cursor: 'pointer', fontSize: 12, fontFamily: 'DM Mono, monospace' }}>
                      {r}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {!item._v2 && (<>
          <div>
            <label style={S.label}>Condition</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input style={{ ...S.input, flex: 1 }} value={draft.conditionQuestion} onChange={e => setDraft(d => ({ ...d, conditionQuestion: e.target.value }))} placeholder="Question (e.g. Is this applicable?)" />
              <input style={{ ...S.input, width: 140 }} value={draft.conditionAnswer} onChange={e => setDraft(d => ({ ...d, conditionAnswer: e.target.value }))} placeholder="Required answer" />
            </div>
          </div>

          {allItems.filter(x => x.id !== item.id && !x.id.startsWith('new-')).length > 0 && (
            <div>
              <label style={S.label}>Parent Item (condition source)</label>
              <select style={S.input} value={draft.conditionParent || ''} onChange={e => setDraft(d => ({ ...d, conditionParent: e.target.value }))}>
                <option value="">None</option>
                {allItems.filter(x => x.id !== item.id && !x.id.startsWith('new-')).map(x => (
                  <option key={x.id} value={x.id}>{x.name || 'Untitled'}</option>
                ))}
              </select>
            </div>
          )}
          </>)}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => onToggleExpand()} style={S.btnSecondary}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={S.btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── AddItemRow ───────────────────────────────────────────────────────────────
function AddItemRow({ type, defaultCategory, onAdd, onCancel, v2 = false }) {
  const [name, setName] = useState('')
  const [category, setCategory] = useState(defaultCategory || CAT_FULL_KIT)
  const inputRef = useRef()
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50) }, [])

  function submit() {
    const n = name.trim()
    if (!n) { inputRef.current?.focus(); return }
    onAdd({ name: n, category, drawingReleases: [] })
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', background: 'var(--aj-bg-subtle)', borderRadius: 4, marginTop: 4 }}>
      <input
        ref={inputRef}
        style={{ ...S.input, flex: 1 }}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel() }}
        placeholder="New item name"
      />
      {type === 'ms' && !v2 && (
        <select style={{ ...S.input, width: 130 }} value={category} onChange={e => setCategory(e.target.value)}>
          {CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
      )}
      <button onClick={submit} style={S.btnPrimary}>Add</button>
      <button onClick={onCancel} style={{ ...S.btnSecondary, padding: '6px 10px' }}>✕</button>
    </div>
  )
}

// ─── New Template Modal ───────────────────────────────────────────────────────
function NewMsModal({ onCreate, onClose }) {
  const [name, setName] = useState('')
  const [phase, setPhase] = useState('')
  const [behavior, setBehavior] = useState('Standard')
  const [saving, setSaving] = useState(false)
  const nameRef = useRef()
  useEffect(() => { setTimeout(() => nameRef.current?.focus(), 50) }, [])

  async function handleCreate() {
    const n = name.trim()
    if (!n) return
    setSaving(true)
    try {
      const rec = await createDeliverableType({ name: n, phase: phase || null, behavior, teamSize: '', quickLinks: [] })
      onCreate(normalizeMsTemplate(rec))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: 24, width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 15, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, color: 'var(--aj-text)' }}>New Milestone Template</div>
        <div>
          <label style={S.label}>Template Name</label>
          <input ref={nameRef} style={S.input} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreate()} placeholder="e.g. Design Development" />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Phase</label>
            <select style={S.input} value={phase} onChange={e => setPhase(e.target.value)}>
              <option value="">—</option>
              {PHASES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Behavior</label>
            <select style={S.input} value={behavior} onChange={e => setBehavior(e.target.value)}>
              {BEHAVIORS.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={S.btnSecondary}>Cancel</button>
          <button onClick={handleCreate} disabled={saving || !name.trim()} style={S.btnPrimary}>{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  )
}

function NewDsModal({ memberId, onCreate, onClose }) {
  const [number, setNumber] = useState('')
  const [title, setTitle] = useState('')
  const [discipline, setDiscipline] = useState('General')
  const [saving, setSaving] = useState(false)
  const numRef = useRef()
  useEffect(() => { setTimeout(() => numRef.current?.focus(), 50) }, [])

  async function handleCreate() {
    const n = number.trim()
    const t = title.trim()
    if (!n || !t) return
    setSaving(true)
    try {
      const rec = await createDrawingSheetTemplate({ number: n, title: t, discipline, memberId })
      onCreate(normalizeDsTemplate(rec))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 15, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, color: 'var(--aj-text)' }}>New Drawing Sheet Template</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ width: 100 }}>
            <label style={S.label}>Sheet Number</label>
            <input ref={numRef} style={S.input} value={number} onChange={e => setNumber(e.target.value)} placeholder="A-101" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Sheet Title</label>
            <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreate()} placeholder="FLOOR PLAN" />
          </div>
        </div>
        <div>
          <label style={S.label}>Discipline</label>
          <select style={S.input} value={discipline} onChange={e => setDiscipline(e.target.value)}>
            {DISCIPLINES.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={S.btnSecondary}>Cancel</button>
          <button onClick={handleCreate} disabled={saving || !number.trim() || !title.trim()} style={S.btnPrimary}>{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Push to Live Modal ───────────────────────────────────────────────────────
function PushModal({ state, onConfirm, onClose }) {
  if (!state || state === 'analyzing') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: 28, width: 380, textAlign: 'center', fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)' }}>
          <div style={{ marginBottom: 12, fontSize: 15, fontWeight: 600 }}>Push to Live</div>
          <div style={{ color: 'var(--aj-text-mid)', fontSize: 13 }}>Analyzing active milestones…</div>
        </div>
      </div>
    )
  }
  if (state === 'pushing') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: 28, width: 380, textAlign: 'center', fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)' }}>
          <div style={{ marginBottom: 12, fontSize: 15, fontWeight: 600 }}>Pushing…</div>
          <div style={{ color: 'var(--aj-text-mid)', fontSize: 13 }}>Updating live checklist items</div>
        </div>
      </div>
    )
  }
  const { liveParents, newItems, orphaned } = state
  const hasChanges = newItems.length > 0 || orphaned.length > 0
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 14, fontFamily: 'DM Sans, sans-serif' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--aj-text)' }}>Sync open milestones</div>
        {liveParents.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', padding: '8px 0' }}>
            No open milestone of this type carries the new checklist yet. Nothing to sync — new milestones pick the template up on their own.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--aj-text-mid)' }}>
              Affects <strong>{liveParents.length}</strong> open milestone{liveParents.length !== 1 ? 's' : ''}. Ticked items are never touched.
            </div>
            {newItems.length > 0 && (
              <div style={{ fontSize: 13, color: '#2e7d32' }}>
                + {newItems.length} new item{newItems.length !== 1 ? 's' : ''} will be added
              </div>
            )}
            {orphaned.length > 0 && (
              <div style={{ fontSize: 13, color: '#c62828' }}>
                − {orphaned.length} removed item{orphaned.length !== 1 ? 's' : ''} will be deleted from live checklists
              </div>
            )}
            {!hasChanges && (
              <div style={{ fontSize: 13, color: 'var(--aj-text-mid)' }}>All live items are already in sync. No changes needed.</div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={S.btnSecondary}>Cancel</button>
          {liveParents.length > 0 && hasChanges && (
            <button onClick={onConfirm} style={S.btnPrimary}>Apply {newItems.length + orphaned.length} change{(newItems.length + orphaned.length) !== 1 ? 's' : ''}</button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Editor view ──────────────────────────────────────────────────────────────
// CHECKLISTS ARE LOCKED (Matt, 2026-10-17): milestone and drawing-sheet checklists are now managed
// with Claude while AlderFLOW is built. Milestones will carry logic tied to their steps, and a step
// edited or deleted here without knowing would break it. Read-only in the app; the server refuses
// edits too (api/checklist-templates.js).
const CHECKLISTS_LOCKED = true
const LOCK_NOTE = 'Checklists are managed with Claude for now, so the logic tied to each step stays intact. Ask Claude to change this one.'

function EditorView({ template, type, onBack, addToast }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [adding, setAdding] = useState(false)
  const [addingCategory, setAddingCategory] = useState(CAT_FULL_KIT)
  const [relFilter, setRelFilter] = useState('All')   // ds: All | DAPC | ...
  const [pushState, setPushState] = useState(null)    // null | 'analyzing' | {...} | 'pushing'
  const [dsView, setDsView] = useState('checklist')   // ds only: 'checklist' | 'titleblock'
  const [displayName, setDisplayName] = useState(template.name)  // ms only — renameable
  const [renaming, setRenaming] = useState(false)
  const autosaveTimer = useRef(null)
  const prevSortOrders = useRef({})                   // id → sortOrder before drag

  async function handleRename() {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    const v = await promptDialog({ title: 'Rename milestone template', defaultValue: displayName, required: true })
    if (v === null) return
    const trimmed = v.trim()
    if (!trimmed || trimmed === displayName) return
    setRenaming(true)
    try {
      await renameDeliverableType(template.id, trimmed)
      setDisplayName(trimmed)
      addToast('Renamed')
    } catch (e) {
      addToast('Rename failed', 'error')
    } finally {
      setRenaming(false)
    }
  }

  // Load items on mount
  useEffect(() => {
    setLoading(true)
    // Both kinds read the v2 Supabase rows now (steps 3 + 4, docs/CHECKLIST_PORT.md). The Airtable
    // template readers stay imported only until the proxy goes at sheet 7.
    const kind = type === 'ms' ? 'milestone' : 'sheet'
    const load = listV2TemplateItems(template.id, kind).then(rows => rows.map(normalizeV2Item))
    load.then(list => {
      const normalized = list.sort((a, b) => a.sortOrder - b.sortOrder)
      setItems(normalized)
      prevSortOrders.current = Object.fromEntries(normalized.map(i => [i.id, i.sortOrder]))
      setLoading(false)
    })
  }, [template.id, type])

  // Autosave sort orders after drag
  function scheduleAutosave(newItems) {
    clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(async () => {
      await reorderV2TemplateItems(newItems.filter(i => !i.id.startsWith('new-')).map(i => i.id), type === 'ms' ? 'milestone' : 'sheet').catch(() => {})
      prevSortOrders.current = Object.fromEntries(newItems.map(x => [x.id, x.sortOrder]))
      return
      const toSave = newItems.filter(i => !i.id.startsWith('new-') && prevSortOrders.current[i.id] !== i.sortOrder)
      for (let i = 0; i < toSave.length; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, 150))
        await patchItemSortOrder(toSave[i].id, toSave[i].sortOrder).catch(() => {})
      }
      prevSortOrders.current = Object.fromEntries(newItems.map(x => [x.id, x.sortOrder]))
    }, 1200)
  }

  function handleReorder(newItems) {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    const reordered = newItems.map((item, idx) => ({ ...item, sortOrder: idx * 10 }))
    setItems(reordered)
    scheduleAutosave(reordered)
  }

  // Per-category reorder: re-insert reordered category items at their original positions
  // in the full sorted list, then renumber everything sequentially.
  function handleCategoryReorder(category, newCatItems) {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    const allSorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder)
    let catIdx = 0
    const merged = allSorted.map(item =>
      item.category === category ? newCatItems[catIdx++] : item
    )
    handleReorder(merged)
  }

  async function handleAddItem({ name, category, drawingReleases }) {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    const nextOrder = items.length > 0 ? Math.max(...items.map(i => i.sortOrder)) + 10 : 0
    const newItem = {
      id: `new-${Date.now()}`,
      name, summary: '',
      category: type === 'ms' ? category : CAT_FULL_KIT,
      sortOrder: nextOrder,
      drawingReleases: type === 'ds' ? drawingReleases : [],
      conditionQuestion: '', conditionAnswer: '', conditionParent: null,
      exampleImages: [], _dirty: false, _imagesDirty: false,
    }
    setAdding(false)
    setItems(prev => [...prev, newItem])
    try {
      {
        const row = await addV2TemplateItem(template.id, name, '', type === 'ms' ? 'milestone' : 'sheet', type === 'ds' ? drawingReleases : undefined)
        setItems(prev => prev.map(x => x.id === newItem.id ? normalizeV2Item(row) : x))
        prevSortOrders.current[row.id] = row.sort_order
        return
      }
      const rec = await saveTemplateItem(newItem, template.id, 'Drawing')
      setItems(prev => prev.map(x => x.id === newItem.id ? { ...normalizeItem(rec), sortOrder: nextOrder } : x))
      prevSortOrders.current[rec.id] = nextOrder
    } catch (e) {
      setItems(prev => prev.filter(x => x.id !== newItem.id))
      addToast('Failed to add item', 'error')
    }
  }

  async function handleSaveItem(updatedItem) {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    const type2 = type === 'ms' ? 'Deliverable' : 'Drawing'
    try {
      {
        const patch = { name: updatedItem.name, summary: updatedItem.summary }
        if (type === 'ds') patch.drawingReleases = updatedItem.drawingReleases || []
        const row = await updateV2TemplateItem(updatedItem.id, patch, type === 'ms' ? 'milestone' : 'sheet')
        setItems(prev => prev.map(x => x.id === updatedItem.id ? { ...normalizeV2Item(row), sortOrder: updatedItem.sortOrder } : x))
        setExpandedId(null)
        addToast('Saved')
        return
      }
      const rec = await saveTemplateItem(updatedItem, template.id, type2)
      const saved = normalizeItem(rec)
      setItems(prev => prev.map(x => x.id === updatedItem.id || x.id === rec.id ? { ...saved, sortOrder: updatedItem.sortOrder } : x))
      setExpandedId(null)
      addToast('Saved')
    } catch (e) {
      addToast('Save failed', 'error')
    }
  }

  async function handleDeleteItem(item) {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    const yes = await confirmDialog({ title: 'Delete item?', message: `Delete "${item.name}"?`, confirmLabel: 'Delete', danger: true })
    if (!yes) return
    setItems(prev => prev.filter(x => x.id !== item.id))
    if (!item.id.startsWith('new-')) {
      await removeV2TemplateItem(item.id, type === 'ms' ? 'milestone' : 'sheet').catch(() => addToast('Delete failed', 'error'))
    }
  }

  async function handlePushToLive() {
    if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return }
    setPushState('analyzing')
    try {
      const result = await analyzePushV2(template.id, type === 'ms' ? 'milestone' : 'sheet')
      setPushState(result)
    } catch (e) {
      setPushState(null)
      addToast('Analysis failed', 'error')
    }
  }

  async function handleConfirmPush() {
    if (!pushState || typeof pushState !== 'object' || pushState === 'analyzing' || pushState === 'pushing') return
    const { liveParents, newItems, orphaned } = pushState
    setPushState('pushing')
    try {
      await doPushV2(template.id, type === 'ms' ? 'milestone' : 'sheet')
      setPushState(null)
      addToast(`Synced ${liveParents.length} ${type === 'ms' ? 'open milestone' : 'sheet'}${liveParents.length !== 1 ? 's' : ''}`)
    } catch (e) {
      setPushState(null)
      addToast('Push failed', 'error')
    }
  }

  // DS: items filtered by release pill
  const dsDisplayItems = type === 'ds'
    ? items.filter(item => relFilter === 'All' || item.drawingReleases.includes(relFilter))
    : []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {CHECKLISTS_LOCKED && (
        <div style={{ margin: '0 0 12px', padding: '10px 14px', borderRadius: 10, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', fontSize: 13, color: 'var(--aj-text-mid)' }}>
          {LOCK_NOTE}
        </div>
      )}
      {/* Editor header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 24px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
          ← Back
        </button>
        {type === 'ms' ? (
          <button
            onClick={handleRename}
            disabled={renaming}
            title="Rename this milestone template"
            style={{
              flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', cursor: renaming ? 'default' : 'pointer',
              fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: 0,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
            <span style={{ fontSize: 12, color: 'var(--aj-text-muted)', flexShrink: 0 }}>{renaming ? 'Renaming…' : '✎'}</span>
          </button>
        ) : (
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {template.name}
          </div>
        )}
        {type === 'ds' && (
          <div style={{ display: 'flex', background: 'var(--aj-bg-muted)', borderRadius: 7, padding: 2, gap: 2, flexShrink: 0 }}>
            {[['checklist', 'Checklist'], ['titleblock', 'Titleblock']].map(([v, label]) => (
              <button key={v} onClick={() => setDsView(v)}
                style={{
                  padding: '4px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  fontSize: 12.5, fontFamily: 'DM Sans, sans-serif',
                  background: dsView === v ? 'var(--aj-bg-elevated)' : 'transparent',
                  color: 'var(--aj-text)',
                  boxShadow: dsView === v ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                  fontWeight: dsView === v ? 500 : 400,
                }}
              >{label}</button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          {(type === 'ms' || dsView === 'checklist') && (
            <span style={{ fontSize: 12, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)' }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
          )}
          {type === 'ds' && dsView === 'checklist' && (
            <>
              <button onClick={() => { setAdding(true); setAddingCategory(CAT_FULL_KIT) }} style={S.btnSecondary}>+ Add Item</button>
              <button onClick={handlePushToLive} style={S.btnSecondary} title="Add new items to, and remove deleted items from, every drawing sheet that already has the new checklist. Ticked items are never touched.">Sync sheets</button>
            </>
          )}
          {type === 'ms' && (
            <>
              <button onClick={() => setAdding(true)} style={S.btnSecondary}>+ Add Item</button>
              <button onClick={handlePushToLive} style={S.btnSecondary} title="Add new items to, and remove deleted items from, every open milestone of this type that already has the new checklist. Ticked items are never touched.">Sync open milestones</button>
            </>
          )}
        </div>
      </div>

      {/* Filter row — DS checklist only (release pills); MS has no filter row, uses category sections */}
      {type === 'ds' && dsView === 'checklist' && (
        <div style={{ padding: '10px 24px', borderBottom: '0.5px solid var(--aj-border)', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
          {['All', ...RELEASES].map(r => (
            <button key={r} onClick={() => setRelFilter(r)} style={S.pill(relFilter === r)}>{r}</button>
          ))}
        </div>
      )}

      {/* DS titleblock sub-view */}
      {type === 'ds' && dsView === 'titleblock' && (
        <SheetTemplateTitleblockPanel templateId={template.id} templateName={template.name} addToast={addToast} />
      )}

      {/* Item list */}
      {(type === 'ms' || dsView === 'checklist') && (
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>
        {/* One page per milestone type (build step 6a, docs/OPERATING_MODEL.md), scrolling as one:
            who owes it and how long it usually takes, what it needs before it starts, then the work. */}
        {type === 'ms' && (
          <div style={{ margin: '0 -24px' }}>
            <MilestoneTypeKindCard typeId={template.id} addToast={addToast} />
            <TypeKitEditor typeId={template.id} typeName={displayName} />
            <div style={{ margin: '14px 24px 4px', paddingBottom: 6, borderBottom: '0.5px solid var(--aj-border)', fontSize: 12.5, color: 'var(--aj-text-mid)', fontFamily: 'DM Sans, sans-serif' }}>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--aj-text-muted)', marginRight: 10 }}>The work</span>
              One list, in the order the work happens. Hand-off items (issue the report, send the link) belong at the end. Changes apply to new milestones; use Sync to bring open ones along.
            </div>
          </div>
        )}
        {loading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Loading…</div>
        ) : type === 'ms' ? (
          /* ── Milestone: one flat v2 list (step 3 — no categories, no conditional logic) ── */
          <>
            <DragList
              items={[...items].sort((a, b) => a.sortOrder - b.sortOrder)}
              onReorder={handleReorder}
              renderItem={(item, dragHandleProps, isDragging) => (
                <ItemRow
                  item={item}
                  allItems={items}
                  type={type}
                  expanded={expandedId === item.id}
                  onToggleExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  onSave={handleSaveItem}
                  onDelete={handleDeleteItem}
                  dragHandleProps={dragHandleProps}
                  isDragging={isDragging}
                />
              )}
            />
            {items.length === 0 && !adding && (
              <div style={{ padding: '24px 0', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>No items yet.</div>
            )}
            {adding && (
              <AddItemRow type={type} v2 defaultCategory="Execution" onAdd={handleAddItem} onCancel={() => setAdding(false)} />
            )}
            {!adding && (
              <button
                onClick={() => setAdding(true)}
                style={{ marginTop: 12, background: 'none', border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', width: '100%' }}
              >
                + Add item
              </button>
            )}
          </>
        ) : (
          /* ── Drawing Sheet: flat list with release filter ── */
          <>
            <DragList
              items={dsDisplayItems}
              onReorder={newFiltered => {
                const filteredIds = new Set(dsDisplayItems.map(x => x.id))
                const rest = items.filter(x => !filteredIds.has(x.id))
                handleReorder([...newFiltered, ...rest])
              }}
              renderItem={(item, dragHandleProps, isDragging) => (
                <ItemRow
                  item={item}
                  allItems={items}
                  type={type}
                  expanded={expandedId === item.id}
                  onToggleExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  onSave={handleSaveItem}
                  onDelete={handleDeleteItem}
                  dragHandleProps={dragHandleProps}
                  isDragging={isDragging}
                />
              )}
            />
            {dsDisplayItems.length === 0 && !adding && (
              <div style={{ padding: '24px 0', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                No items{relFilter !== 'All' ? ' in this release' : ' yet'}.
              </div>
            )}
            {adding && (
              <AddItemRow type={type} v2 defaultCategory={CAT_FULL_KIT} onAdd={handleAddItem} onCancel={() => setAdding(false)} />
            )}
            {!adding && (
              <button
                onClick={() => setAdding(true)}
                style={{ marginTop: 12, background: 'none', border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', width: '100%' }}
              >
                + Add item
              </button>
            )}
          </>
        )}
      </div>
      )}

    </div>
  )
}

// ─── List view ────────────────────────────────────────────────────────────────
function ListView({ onOpenTemplate, addToast, currentMemberId, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'milestone')
  const [msTemplates, setMsTemplates] = useState(null)  // null = loading
  const [dsTemplates, setDsTemplates] = useState(null)
  const [search, setSearch] = useState('')
  const [phaseFilter, setPhaseFilter] = useState('All')
  const [disciplineFilter, setDisciplineFilter] = useState('All')
  const [newMsOpen, setNewMsOpen] = useState(false)
  const [newDsOpen, setNewDsOpen] = useState(false)

  useEffect(() => {
    getDeliverableTypes().then(recs => setMsTemplates(recs.map(normalizeMsTemplate)))
    getDrawingSheetTemplates().then(recs => setDsTemplates(recs.map(normalizeDsTemplate)))
  }, [])

  const templates = tab === 'milestone' ? (msTemplates || []) : (dsTemplates || [])
  const loading = tab === 'milestone' ? msTemplates === null : dsTemplates === null

  // Filter templates by search and phase/discipline
  const q = search.toLowerCase()
  const visible = templates.filter(t => {
    if (q && !t.name.toLowerCase().includes(q)) return false
    if (tab === 'milestone' && phaseFilter !== 'All' && t.phase !== phaseFilter) return false
    if (tab === 'drawing' && disciplineFilter !== 'All' && t.discipline !== disciplineFilter) return false
    return true
  })

  // Available phases and disciplines from actual data
  const usedPhases = Array.from(new Set((msTemplates || []).map(t => t.phase).filter(Boolean)))
  const usedDisciplines = Array.from(new Set((dsTemplates || []).map(t => t.discipline).filter(Boolean)))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* List header */}
      <div style={{ padding: '14px 24px 0', flexShrink: 0 }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, minWidth: 0 }}>
          {/* the tabs wrap onto a second row rather than push the window wider than its frame */}
          <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1, minWidth: 0, background: 'var(--aj-bg-muted)', borderRadius: 7, padding: 2, gap: 2 }}>
            {[['milestone','Milestones'], ...ADMIN_TEMPLATE_TABS.filter(t => t.key !== 'wbs').map(t => [t.key, t.label]), ['drawing','Drawing Sheet'],['bim','BIM'],['punch','Punch Guidance'],['submittal','Submittal'],['modeler','Modeler'],['details','Details'],['keynotes','Keynotes'],['abbreviations','Abbreviations'],['sheetNotes','Sheet Notes'],['priority','Priority']].map(([v, label]) => (
              <button key={v} onClick={() => { setTab(v); setSearch(''); setPhaseFilter('All'); setDisciplineFilter('All') }}
                style={{
                  padding: '5px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontFamily: 'DM Sans, sans-serif',
                  background: tab === v ? 'var(--aj-bg-elevated)' : 'transparent',
                  color: 'var(--aj-text)',
                  boxShadow: tab === v ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                  fontWeight: tab === v ? 500 : 400,
                }}
              >{label}</button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          {/* WBS Milestones lives on the Milestones tab as a button, not its own tab (Matt, 2026-09-24):
              it is the other half of how milestones work. Open to anyone who can edit templates (Matt, same day). */}
          {tab === 'milestone' && (
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-wbs-editor'))} style={S.btnSecondary}
              title="Which milestones each phase of a new project gets, their order, how long each takes, and what waits on what">
              WBS Milestones
            </button>
          )}
          {!ADMIN_TAB_KEYS.has(tab) && tab !== 'bim' && tab !== 'gates' && tab !== 'punch' && tab !== 'submittal' && tab !== 'modeler' && tab !== 'details' && tab !== 'keynotes' && tab !== 'priority' && (
            <button onClick={() => { if (CHECKLISTS_LOCKED) { addToast(LOCK_NOTE, 'error'); return } tab === 'milestone' ? setNewMsOpen(true) : setNewDsOpen(true) }} style={{ ...S.btnPrimary, ...(CHECKLISTS_LOCKED ? { opacity: .4 } : null) }} title={CHECKLISTS_LOCKED ? LOCK_NOTE : undefined}>
              + New Template
            </button>
          )}
        </div>

        {/* Search + filter pills (the BIM, Punch Guidance, Submittal, Modeler, and Priority tabs render their own controls) */}
        {!ADMIN_TAB_KEYS.has(tab) && tab !== 'bim' && tab !== 'gates' && tab !== 'punch' && tab !== 'submittal' && tab !== 'modeler' && tab !== 'details' && tab !== 'keynotes' && tab !== 'priority' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <input
            style={{ ...S.input, width: 220 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${tab === 'milestone' ? 'milestone' : 'drawing sheet'} templates…`}
          />
          {tab === 'milestone' && usedPhases.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <button onClick={() => setPhaseFilter('All')} style={S.pill(phaseFilter === 'All')}>All phases</button>
              {usedPhases.map(p => (
                <button key={p} onClick={() => setPhaseFilter(p)} style={S.pill(phaseFilter === p)}>{p}</button>
              ))}
            </div>
          )}
          {tab === 'drawing' && usedDisciplines.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <button onClick={() => setDisciplineFilter('All')} style={S.pill(disciplineFilter === 'All')}>All disciplines</button>
              {usedDisciplines.map(d => (
                <button key={d} onClick={() => setDisciplineFilter(d)} style={S.pill(disciplineFilter === d)}>{d}</button>
              ))}
            </div>
          )}
        </div>
        )}
        <div style={{ borderBottom: '0.5px solid var(--aj-border)' }} />
      </div>

      {/* BIM tab — grouped Supabase-backed list with its own search/add (BimTemplateEditor.jsx) */}
      {ADMIN_TAB_KEYS.has(tab) ? (
        <AdminTemplateLauncher tab={tab} />
      ) : tab === 'bim' ? (
        <BimTemplatesList onOpenTemplate={onOpenTemplate} addToast={addToast} />
      ) : tab === 'punch' ? (
        <PunchRoomGuidanceList addToast={addToast} />
      ) : tab === 'submittal' ? (
        <SubmittalChecklistList addToast={addToast} />
      ) : tab === 'modeler' ? (
        /* Modeler firm library (docs/MODELER.md "FIRM LIBRARY") — Supabase-curated
           types/materials/annotation types every model subscribes to */
        <Suspense fallback={<div style={{ padding: 24, fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading firm library…</div>}>
          <BimFirmLibraryList addToast={addToast} />
        </Suspense>
      ) : tab === 'details' ? (
        /* Firm detail library (docs/MODELER.md "FIRM DETAIL LIBRARY round") — browse,
           search, preview, edit; projects import from AlderCAD */
        <Suspense fallback={<div style={{ padding: 24, fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading detail library…</div>}>
          <FirmDetailLibraryTab addToast={addToast} />
        </Suspense>
      ) : tab === 'keynotes' ? (
        /* Demolition keynotes (docs/DEMO_KEYNOTES.md) — the firm list AlderCAD demolition plans number from */
        <DemoKeynotesList addToast={addToast} />
      ) : tab === 'sheetNotes' ? (
        /* Per-sheet-type notes (docs/bim-plan/28 round 10) — the AlderCAD title strip's notes band */
        <SheetNotesList addToast={addToast} />
      ) : tab === 'abbreviations' ? (
        /* Drawing abbreviations (docs/bim-plan/28 round 7) — the firm list the G003 sheet prints */
        <DrawingAbbreviationsList addToast={addToast} />
      ) : tab === 'priority' ? (
        /* Priority Engine curation (docs/PRIORITY_ENGINE.md Phase 3) — cost-of-delay + effort
           scored once per TYPE so every milestone inherits it. Nothing ranks by this yet. */
        <PriorityScoreEditor />
      ) : (
      <>
      {/* Template rows */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Loading templates…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
            {templates.length === 0 ? 'No templates yet.' : 'No templates match the current filter.'}
          </div>
        ) : (
          visible.map(t => (
            <div
              key={t.id}
              onClick={() => onOpenTemplate({ ...t, type: tab })}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 10px',
                borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--aj-bg-subtle)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', fontWeight: 500 }}>{t.name}</div>
                {tab === 'milestone' && t.phase && (
                  <div style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)', marginTop: 2 }}>{t.phase}</div>
                )}
                {tab === 'drawing' && t.discipline && (
                  <div style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)', marginTop: 2 }}>{t.discipline}</div>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: 'DM Mono, monospace' }}>→</div>
            </div>
          ))
        )}
      </div>

      {newMsOpen && (
        <NewMsModal
          onCreate={tpl => {
            setMsTemplates(prev => [...(prev || []), tpl])
            setNewMsOpen(false)
            addToast('Template created')
            onOpenTemplate({ ...tpl, type: 'milestone' })
          }}
          onClose={() => setNewMsOpen(false)}
        />
      )}
      {newDsOpen && (
        <NewDsModal
          memberId={currentMemberId}
          onCreate={tpl => {
            setDsTemplates(prev => [...(prev || []), tpl])
            setNewDsOpen(false)
            addToast('Template created')
            onOpenTemplate({ ...tpl, type: 'drawing' })
          }}
          onClose={() => setNewDsOpen(false)}
        />
      )}
      </>
      )}
    </div>
  )
}

// ─── Main workspace ───────────────────────────────────────────────────────────
// Templates is a workspace PAGE like Billing or CRM, not a pop-up (Matt, 2026-11-03): it docks
// beside the menu, carries ?ws=templates (so a refresh comes back to it and the activity log sees
// the tool), and Escape does not close it.
function templatesInUrl() {
  try { return new URLSearchParams(window.location.search).get('ws') === 'templates' } catch { return false }
}

export default function TemplatesWorkspace() {
  const [open, setOpen] = useState(templatesInUrl)
  const [view, setView] = useState('list')  // 'list' | 'editor'
  const [activeTemplate, setActiveTemplate] = useState(null)  // { id, name, type }
  const [listTab, setListTab] = useState('milestone')  // initial tab when opening to list view
  const { toasts, addToast } = useToasts()

  // Open/close via CustomEvents
  useEffect(() => {
    function onOpen(e) {
      setOpen(true)
      window.dispatchEvent(new CustomEvent('open-templates'))
      if (e && e.detail && e.detail.templateId) {
        setActiveTemplate({ id: e.detail.templateId, name: e.detail.templateName || '', type: e.detail.templateType || 'milestone' })
        setView('editor')
      } else if (e && e.detail && e.detail.tab) {
        setListTab(e.detail.tab === 'gates' || e.detail.tab === 'wbs' ? 'milestone' : e.detail.tab)   // the Gates tab was retired (step 6a); kits live on each milestone type's page
        setView('list')
      }
    }
    function onClose() {
      setOpen(false)
      setView('list')
      setActiveTemplate(null)
      window.dispatchEvent(new CustomEvent('close-templates'))
    }
    window.addEventListener('open-templates-workspace', onOpen)
    window.addEventListener('close-templates-workspace', onClose)
    return () => {
      window.removeEventListener('open-templates-workspace', onOpen)
      window.removeEventListener('close-templates-workspace', onClose)
    }
  }, [])

  // Reflect the workspace in the URL and the tab title, as every other workspace does.
  useEffect(() => {
    const url = new URL(window.location)
    if (open) {
      url.searchParams.set('ws', 'templates')
      // A frame later: OutlineView's own title effect runs after this one and would put "Projects" back.
      requestAnimationFrame(() => { document.title = 'Alder James Templates' })
      window.dispatchEvent(new CustomEvent('open-templates'))   // lights the menu item after a refresh
    } else if (url.searchParams.get('ws') === 'templates') {
      url.searchParams.delete('ws')
    }
    window.history.replaceState(null, '', url)
  }, [open])

  // Freeze background scroll while open
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  const getMemberId = () => ctx.currentMember?.id || null

  if (!open) return null
  if (!isSeatLoaded() || !(hasModule('admin') || hasModule('checklist-templates'))) return null

  function handleOpenTemplate(tpl) {
    setActiveTemplate(tpl)
    setView('editor')
  }

  function handleBack() {
    setView('list')
    setActiveTemplate(null)
  }

  return (
    <div className="aj-templates-ws" style={{ display: 'flex', flexDirection: 'column' }}>
      <WorkspaceHeader title="Templates" />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-subtle)' }}>
        {/* Content */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {view === 'list' ? (
            <ListView
              key={listTab}
              onOpenTemplate={handleOpenTemplate}
              addToast={addToast}
              currentMemberId={getMemberId()}
              initialTab={listTab}
            />
          ) : activeTemplate?.type === 'bim' ? (
            <BimTemplateEditorView
              template={activeTemplate}
              onBack={handleBack}
              addToast={addToast}
            />
          ) : (
            <EditorView
              template={activeTemplate}
              type={activeTemplate?.type === 'milestone' ? 'ms' : 'ds'}
              onBack={handleBack}
              addToast={addToast}
            />
          )}
        </div>

        <Toasts toasts={toasts} />
      </div>
    </div>
  )
}
